import {
  memo,
  useCallback, useEffect, useMemo, useState,
} from 'react';
import SlotMachinePixi from '../SlotMachinePixi';
import AllWinsBanner from './AllWinsBanner';
import BottomPart from './BottomPart';
import BottomScreen from './BottomScreen';
import MachineScreenBgSVG from './MachineScreenBgSVG';
import MachineSvgDefs from './MachineSvgDefs';
import MultiplierBtn from './MultiplierBtn';
import SecondLayer from './SecondLayer';
import { MultiplierT, MultiplierValues } from './slotConstants';
import SpinButton from './SpinButton';
import SpinResultBlock from './SpinResultBlock';
import SpinsScreen from './SpinsScreen';
import { SlotMachinePixiApp } from '../../../apps/slotMachine';
import { useEnergyContext } from '../../../providers/EnergyProvider';
import MachineBorder from './MachineBorder';

type MachineProps = {
  onSpin: () => void
  isSpinning: boolean
  multiplier: MultiplierT
  setMultiplier: (val: MultiplierT) => void
}
export const getMachineFrameScale = () => {
  const parentSvg = document.getElementById('machine-frame-svg');
  if (!parentSvg) return null;

  const actualWidth = parentSvg.clientWidth;
  const actualHeight = parentSvg.clientHeight;
  const viewBox = parentSvg.getAttribute('viewBox');

  if (!viewBox) return null;

  const [, , viewBoxWidth, viewBoxHeight] = viewBox.split(' ').map(Number);
  return { scaleX: actualWidth / viewBoxWidth, scaleY: actualHeight / viewBoxHeight };
};

function Machine({
  onSpin, isSpinning, multiplier, setMultiplier,
}: MachineProps) {
  const { userEnergy, energyForSpin } = useEnergyContext();
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [scale, setScale] = useState({ scaleX: 1, scaleY: 1 });

  const isSafari = useMemo(() => /^((?!chrome|android).)*safari/i.test(navigator.userAgent), []);
  const resize = useCallback(() => {
    const slotGame = SlotMachinePixiApp.getInstance().getApp();

    const foreignMachine = document.getElementById('machine-foreign');
    const parentSvg = document.getElementById('machine-frame-svg');
    const screenSvg = document.getElementById('svg_63');
    const screenSvgHeight = document.getElementById('svg_66');
    const screenSvgWidth = document.getElementById('svg_32');
    const divInside = document.getElementById('machine-content');
    if (foreignMachine && parentSvg && screenSvg && divInside && screenSvgWidth && screenSvgHeight) {
      // const screenRect = screenSvg.getBoundingClientRect();
      const screenRectWid = screenSvgWidth.getBoundingClientRect();
      const screenRectHeight = screenSvgHeight.getBoundingClientRect();
      const scaleData = getMachineFrameScale();
      if (scaleData) {
        const { scaleX, scaleY } = scaleData;
        setScale({ scaleX, scaleY });

        foreignMachine.setAttribute('width', (screenRectWid.width / scaleX).toString());
        foreignMachine.setAttribute('height', (screenRectHeight.height / scaleY).toString());
        // set position to div inside foreignObject
        divInside.style.width = `${screenRectWid.width / scaleX}px`;
        divInside.style.height = `${screenRectHeight.height / scaleY}px`;
        divInside.style.position = 'fixed';
        divInside.style.top = '0';
        divInside.style.left = '0';

        if (isSafari) {
          const { height: resizeHeight, width: resizeWidth } = screenSvg.getBoundingClientRect();

          if (resizeHeight && resizeWidth) {
            slotGame.renderer.resize(resizeWidth, resizeHeight);
          }
          if (scaleX && scaleY) {
            slotGame.stage.scale.set(scaleX, scaleY);
          }
        }
      }
    }
  }, [isSafari]);
  useEffect(() => {
    resize();
    window.addEventListener('resize', resize);
  }, [resize]);

  const handleChangeMultiplier = useCallback(() => {
    setIsAnimating(true);
    const indexOfCurrentMultiplier = MultiplierValues.indexOf(multiplier);
    const hasEnergyForMultipleSpins = energyForSpin * MultiplierValues[indexOfCurrentMultiplier + 1] <= userEnergy;
    if (indexOfCurrentMultiplier + 1 === MultiplierValues.length || !hasEnergyForMultipleSpins) {
      setMultiplier(MultiplierValues[0]);
    } else {
      setMultiplier(MultiplierValues[indexOfCurrentMultiplier + 1]);
    }
    setTimeout(() => {
      setIsAnimating(false);
    }, 1000);
  }, [energyForSpin, multiplier, setMultiplier, MultiplierValues, userEnergy]);

  useEffect(() => {
    const indexOfCurrentMultiplier = MultiplierValues.indexOf(multiplier);
    const hasEnergyForMultipleSpins = energyForSpin * MultiplierValues[indexOfCurrentMultiplier] <= userEnergy;
    if (!hasEnergyForMultipleSpins) {
      const valueWithEnoughEnergy = [...MultiplierValues].reverse().find((val) => val * energyForSpin <= userEnergy);
      setMultiplier(valueWithEnoughEnergy || MultiplierValues[0]);
    }
  }, [userEnergy, energyForSpin]);

  return (
    <>
      <AllWinsBanner multiplier={multiplier} scale={scale} isSafari={isSafari} isAnimating={isAnimating} />
      <svg
        id="machine-frame-svg"
        className="w-full h-auto max-w-full"
        // viewBox="0 0 374 486"
        viewBox="0 0 374 566"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
        fill="none"
        style={{
          overflow: 'hidden',
        }}
      >
        <foreignObject
          x="-5"
          y={(window?.Telegram.WebApp.platform === 'android' || window?.Telegram.WebApp.platform === 'unknown') ? '-10' : '-25'}
          width="100%"
          height="100%"
          className="pointer-events-none"
        >
          <MachineScreenBgSVG />
        </foreignObject>
        <foreignObject x="20" y="35" id="machine-foreign">
          <div
            id="machine-content"
            className={`z-[2] relative overflow-hidden ${isSafari ? 'machine-content-safari' : ''}`}
          >
            <SlotMachinePixi />
          </div>
        </foreignObject>
        <g>
          <SecondLayer />
          <svg y="363" x="69.45621" id="svg_220" fill="none" viewBox="0 0 216 104" height="104" width="236" xmlns="http://www.w3.org/2000/svg">
            <foreignObject x={0} y={0} height="100%" width="100%">
              <SpinButton onClick={onSpin} isSpinning={isSpinning} />
            </foreignObject>
          </svg>
        </g>
        <g
          id="svg_164"
        >
          <svg width="136" height="24" viewBox="0 0 140 24" fill="none" id="svg_4" x="119.18385" y="305.41553" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#svg_9)" id="svg_147">
              <path d="m1.34214,22.9748c-0.28203,0 -0.45124,-0.1695 -0.50765,-0.2825c-0.05641,-0.0565 -0.16922,-0.2825 0,-0.565l10.37861,-20.90692c0.1129,-0.22602 0.3385,-0.33903 0.5641,-0.33903l112.5298,0c0.225,0 0.451,0.11301 0.507,0.33903l10.04,20.90692c0.113,0.1695 0.057,0.3955 -0.056,0.565c-0.113,0.1695 -0.282,0.2825 -0.508,0.2825l-132.94786,0z" fill="#0C056B" id="svg_152" />
              <path d="m1.34214,22.9748c-0.28203,0 -0.45124,-0.1695 -0.50765,-0.2825c-0.05641,-0.0565 -0.16922,-0.2825 0,-0.565l10.37861,-20.90692c0.1129,-0.22602 0.3385,-0.33903 0.5641,-0.33903l112.5298,0c0.225,0 0.451,0.11301 0.507,0.33903l10.04,20.90692c0.113,0.1695 0.057,0.3955 -0.056,0.565c-0.113,0.1695 -0.282,0.2825 -0.508,0.2825l-132.94786,0z" fill="url(#svg_3)" id="svg_151" />
              <path d="m132.542,22.3534l-129.39484,0c-0.84609,0 -1.41014,-0.9041 -1.0153,-1.6387l9.30694,-18.64664c0.1692,-0.39553 0.5641,-0.62155 1.0153,-0.62155l111.1189,0c0.452,0 0.846,0.22602 1.016,0.62155l8.968,18.64664c0.339,0.7911 -0.169,1.6387 -1.015,1.6387zm-8.235,-22.03699l-112.5298,0c-0.4512,0 -0.846,0.22602 -1.0153,0.62155l-10.09659,20.11584c-0.56406,1.1301 0.28203,2.4297 1.52295,2.4297l131.25574,0c1.241,0 2.087,-1.2996 1.523,-2.4297l-9.645,-20.05933c-0.169,-0.39554 -0.621,-0.67806 -1.015,-0.67806z" fill="url(#svg_2)" id="svg_148" />
            </g>
            <defs>
              <clipPath id="svg_9">
                <rect width="134.697" height="23.2236" fill="white" y="0.3169" x="0.49609" id="svg_12" />
              </clipPath>
            </defs>
            <foreignObject
              x="0"
              y="0"
              width="100%"
              height="100%"
            >
              <BottomScreen multiplier={multiplier} isClickable onMultiplierClick={handleChangeMultiplier} isAnimating={isAnimating} isSafari={isSafari} />
            </foreignObject>
          </svg>
        </g>
      </svg>
      <svg
        id="machine-frame-svg-second"
        className="w-full h-auto max-w-full"
        viewBox="0 0 374 566"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
        fill="none"
        style={{
          overflow: 'hidden',
        }}
      >
        <defs>
          <MachineSvgDefs />
        </defs>
        <g>
          <g id="svg_194">
            <MachineBorder />
            <MultiplierBtn multiplier={multiplier} handleChangeMultiplier={handleChangeMultiplier} isAnimating={isAnimating} isSafari={isSafari} />
            <SpinResultBlock isSpinning={isSpinning} />
          </g>
        </g>
      </svg>
      <div className="mt-[-104px] flex justify-center flex-col items-center">
        <div className="z-3 translate-y-[10px]">
          <SpinsScreen />
        </div>
        <div>
          <BottomPart />
        </div>
      </div>
    </>
  );
}
export default memo(Machine);
